/** 现场实操任务 Mock数据 —— 以 BLU-C0047 冷冻大蒜为主线 */

import type { FieldTask, ChecklistItem } from '../types';
import { TaskType, TaskStatus, TaskPriority } from '../types';
import { NODE_NAMES } from '../utils/constants';

// ===== 主线故事：BLU-C0047 冷冻大蒜 20 吨 → 迪拜 =====
// 一条完整的履约链路：扫码绑定 → 装柜 → 质检 → 签收

const MAIN_CONTAINER = 'BLU-C0047';
const MAIN_PRODUCT = '冷冻大蒜';
const MAIN_WEIGHT = '20 吨';
const MAIN_ORIGIN = '山东金乡';
const MAIN_DEST = '迪拜';

function makeChecklist(type: TaskType): ChecklistItem[] {
  switch (type) {
    case TaskType.SCAN_BIND_IOT:
      return [
        { id: 'CL001', label: '扫描集装箱条码', required: true, completed: false, photoRequired: true },
        { id: 'CL002', label: '确认IoT设备编号', required: true, completed: false, photoRequired: false },
        { id: 'CL003', label: '安装IoT设备到集装箱', required: true, completed: false, photoRequired: true },
        { id: 'CL004', label: '验证设备信号连接', required: true, completed: false, photoRequired: false },
        { id: 'CL005', label: '确认温湿度数据上报', required: true, completed: false, photoRequired: false },
      ];
    case TaskType.LOADING_SEAL:
      return [
        { id: 'CL001', label: '检查集装箱完好性', required: true, completed: false, photoRequired: true },
        { id: 'CL002', label: '核对装箱清单', required: true, completed: false, photoRequired: false },
        { id: 'CL003', label: '监督装柜过程', required: true, completed: false, photoRequired: true },
        { id: 'CL004', label: '安装铅封', required: true, completed: false, photoRequired: true },
        { id: 'CL005', label: '记录铅封编号', required: true, completed: false, photoRequired: false },
      ];
    case TaskType.NODE_INSPECTION:
      return [
        { id: 'CL001', label: '检查温度记录', required: true, completed: false, photoRequired: false },
        { id: 'CL002', label: '检查湿度记录', required: true, completed: false, photoRequired: false },
        { id: 'CL003', label: '外观检查拍照', required: true, completed: false, photoRequired: true },
        { id: 'CL004', label: '取样检测（如有）', required: false, completed: false, photoRequired: true },
        { id: 'CL005', label: '填写质检报告', required: true, completed: false, photoRequired: false },
      ];
    case TaskType.SIGN_UNBOX:
      return [
        { id: 'CL001', label: '核对收货信息', required: true, completed: false, photoRequired: false },
        { id: 'CL002', label: '检查铅封完整性', required: true, completed: false, photoRequired: true },
        { id: 'CL003', label: '监督开箱过程', required: true, completed: false, photoRequired: true },
        { id: 'CL004', label: '清点货物数量', required: true, completed: false, photoRequired: false },
        { id: 'CL005', label: '签署收货单据', required: true, completed: false, photoRequired: true },
      ];
  }
}

// ===== 主线任务：BLU-C0047 完整履约链 =====
const mainTasks: FieldTask[] = [
  {
    id: 'TSK001',
    taskNo: 'TASK-2025-0002',
    type: TaskType.SCAN_BIND_IOT,
    containerNo: MAIN_CONTAINER,
    nodeId: 6,
    nodeName: 'N06-提空柜',
    assignedTo: '李产地',
    status: TaskStatus.TODO,
    priority: TaskPriority.HIGH,
    deadline: new Date(Date.now() + 1 * 86400000).toISOString(),
    createdAt: new Date(Date.now() - 2 * 86400000).toISOString(),
    checklist: makeChecklist(TaskType.SCAN_BIND_IOT),
    location: `${MAIN_ORIGIN}加工厂`,
  },
  {
    id: 'TSK002',
    taskNo: 'TASK-2025-0007',
    type: TaskType.LOADING_SEAL,
    containerNo: MAIN_CONTAINER,
    nodeId: 7,
    nodeName: 'N07-装柜',
    assignedTo: '张装柜',
    status: TaskStatus.TODO,
    priority: TaskPriority.HIGH,
    deadline: new Date(Date.now() + 2 * 86400000).toISOString(),
    createdAt: new Date(Date.now() - 1 * 86400000).toISOString(),
    checklist: makeChecklist(TaskType.LOADING_SEAL),
    location: `${MAIN_ORIGIN}冷链仓库`,
  },
  {
    id: 'TSK003',
    taskNo: 'TASK-2025-0011',
    type: TaskType.NODE_INSPECTION,
    containerNo: MAIN_CONTAINER,
    nodeId: 5,
    nodeName: 'N05-订舱',
    assignedTo: '王清关',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    deadline: new Date(Date.now() + 3 * 86400000).toISOString(),
    createdAt: new Date(Date.now() - 1 * 86400000).toISOString(),
    checklist: makeChecklist(TaskType.NODE_INSPECTION),
    location: `${MAIN_ORIGIN}质检中心`,
  },
  {
    id: 'TSK004',
    taskNo: 'TASK-2025-0019',
    type: TaskType.SIGN_UNBOX,
    containerNo: MAIN_CONTAINER,
    nodeId: 15,
    nodeName: 'N15-签收',
    assignedTo: '赵迪拜',
    status: TaskStatus.TODO,
    priority: TaskPriority.URGENT,
    deadline: new Date(Date.now() + 0.5 * 86400000).toISOString(),
    createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
    checklist: makeChecklist(TaskType.SIGN_UNBOX),
    location: `${MAIN_DEST}港口`,
  },
];

// ===== 其他任务：不同柜号，让列表看起来更丰富 =====
const otherTasks: FieldTask[] = [
  {
    id: 'TSK005',
    taskNo: 'TASK-2025-0003',
    type: TaskType.SCAN_BIND_IOT,
    containerNo: 'BLU-C0051',
    nodeId: 6,
    nodeName: 'N06-提空柜',
    assignedTo: '李产地',
    status: TaskStatus.IN_PROGRESS,
    priority: TaskPriority.MEDIUM,
    deadline: new Date(Date.now() + 1 * 86400000).toISOString(),
    createdAt: new Date(Date.now() - 3 * 86400000).toISOString(),
    checklist: makeChecklist(TaskType.SCAN_BIND_IOT),
    location: '青岛前湾港2号堆场',
  },
  {
    id: 'TSK006',
    taskNo: 'TASK-2025-0009',
    type: TaskType.LOADING_SEAL,
    containerNo: 'BLU-C0049',
    nodeId: 7,
    nodeName: 'N07-装柜',
    assignedTo: '张装柜',
    status: TaskStatus.COMPLETED,
    priority: TaskPriority.LOW,
    deadline: new Date(Date.now() - 1 * 86400000).toISOString(),
    createdAt: new Date(Date.now() - 5 * 86400000).toISOString(),
    checklist: makeChecklist(TaskType.LOADING_SEAL),
    location: '宁波舟山港5号泊位',
  },
  {
    id: 'TSK007',
    taskNo: 'TASK-2025-0015',
    type: TaskType.NODE_INSPECTION,
    containerNo: 'BLU-C0053',
    nodeId: 10,
    nodeName: 'N10-离港',
    assignedTo: '王清关',
    status: TaskStatus.OVERDUE,
    priority: TaskPriority.URGENT,
    deadline: new Date(Date.now() - 2 * 86400000).toISOString(),
    createdAt: new Date(Date.now() - 7 * 86400000).toISOString(),
    checklist: makeChecklist(TaskType.NODE_INSPECTION),
    location: '上海洋山港1号堆场',
  },
  {
    id: 'TSK008',
    taskNo: 'TASK-2025-0020',
    type: TaskType.SIGN_UNBOX,
    containerNo: 'BLU-C0045',
    nodeId: 15,
    nodeName: 'N15-签收',
    assignedTo: '赵迪拜',
    status: TaskStatus.TODO,
    priority: TaskPriority.HIGH,
    deadline: new Date(Date.now() + 4 * 86400000).toISOString(),
    createdAt: new Date(Date.now() - 6 * 86400000).toISOString(),
    checklist: makeChecklist(TaskType.SIGN_UNBOX),
    location: '深圳盐田港3号冷链仓',
  },
  {
    id: 'TSK009',
    taskNo: 'TASK-2025-0022',
    type: TaskType.LOADING_SEAL,
    containerNo: 'BLU-C0055',
    nodeId: 7,
    nodeName: 'N07-装柜',
    assignedTo: '张装柜',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    deadline: new Date(Date.now() + 5 * 86400000).toISOString(),
    createdAt: new Date(Date.now() - 1 * 86400000).toISOString(),
    checklist: makeChecklist(TaskType.LOADING_SEAL),
    location: '广州南沙港冷链中心',
  },
  {
    id: 'TSK010',
    taskNo: 'TASK-2025-0025',
    type: TaskType.NODE_INSPECTION,
    containerNo: 'BLU-C0058',
    nodeId: 8,
    nodeName: 'N08-报关',
    assignedTo: '王清关',
    status: TaskStatus.COMPLETED,
    priority: TaskPriority.LOW,
    deadline: new Date(Date.now() - 3 * 86400000).toISOString(),
    createdAt: new Date(Date.now() - 8 * 86400000).toISOString(),
    checklist: makeChecklist(TaskType.NODE_INSPECTION),
    location: '天津港东疆保税区',
  },
];

export const mockTasks: FieldTask[] = [...mainTasks, ...otherTasks];
